import React, { useEffect, useState } from "react";
import { RefreshCw, Clock, CalendarDays, Save } from "lucide-react";
import BiasGraphic from "./BiasGraphic";
import { fmtPrice, fmtCountdown, planState } from "../../lib/format";
import { toast } from "sonner";
import api from "../../lib/api";

const DIR_STYLE = {
  LONG: { c: "#22c55e", bg: "rgba(34,197,94,.14)" },
  SHORT: { c: "#ef4444", bg: "rgba(239,68,68,.14)" },
  WAIT: { c: "#a78bfa", bg: "rgba(167,139,250,.14)" },
  "NO TRADE": { c: "#22d3ee", bg: "rgba(34,211,238,.14)" },
};

export default function ActiveTradingPlan({ analysis, onRecalculate, loading }) {
  const plan = analysis?.plan;
  const dir = analysis?.direction || "WAIT";
  const st = DIR_STYLE[dir] || DIR_STYLE.WAIT;
  const [countdown, setCountdown] = useState("--:--:--");

  useEffect(() => {
    if (!plan?.validUntilTs) return;
    const tick = () => setCountdown(fmtCountdown(plan.validUntilTs));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [plan?.validUntilTs]);

  const save = async () => {
    try {
      await api.savePlan(analysis.symbol, analysis.interval);
      toast.success(`Plan saved for ${analysis.symbol}`);
    } catch { toast.error("Could not save plan"); }
  };

  return (
    <div className="ym-panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-bold">Rencana Trading Aktif</h3>
        <span className="pill" style={{ color: planState(plan?.status).color, background: "var(--panel-2)", borderColor: "var(--border)" }}>
          {planState(plan?.status).label}
        </span>
      </div>

      {/* direction + graphic */}
      <div className="flex items-center gap-3 rounded-xl p-3 mb-3" style={{ background: st.bg, border: `1px solid ${st.c}44` }}>
        <BiasGraphic direction={dir} size={54} />
        <div className="leading-tight">
          <div className="text-[22px] font-extrabold" style={{ color: st.c }}>{dir}</div>
          <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{analysis?.symbol} · {analysis?.interval?.toUpperCase()} · {plan?.provider}{plan?.isConfirmed4H === false ? " · Snapshot mekanis — bukan setup terkonfirmasi" : ""}</div>
        </div>
      </div>

      {/* date + validity */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <InfoBox icon={CalendarDays} label="Tanggal Rencana" value={plan?.datePlan || "—"} sub={`Dibuat ${plan?.timeCreated || "—"}`} />
        <InfoBox icon={Clock} label="Berlaku Sampai" value={plan?.validUntil || "—"} sub={`Tersisa ${countdown}`} highlight={countdown !== "EXPIRED"} />
      </div>

      {/* levels */}
      {plan?.entryLow ? (
        <div className="space-y-1.5 mb-3">
          <Row label={plan.entryZoneFollowsLivePrice ? "Zona Entry (mengikuti harga live)" : "Zona Entry"} value={`${fmtPrice(plan.entryLow)} – ${fmtPrice(plan.entryHigh)}`} color="#8b5cf6" />
          <Row label="Stop Loss" value={fmtPrice(plan.stopLoss)} color="#ef4444" />
          <Row label="TP1" value={fmtPrice(plan.tp1)} color="#22c55e" />
          <Row label="TP2" value={fmtPrice(plan.tp2)} color="#16a34a" />
          <Row label="TP3" value={fmtPrice(plan.tp3)} color="#15803d" />
          <Row label="Risk : Reward" value={`1 : ${plan.riskReward} (constructed)`} color="#a78bfa" bold />
        </div>
      ) : (
        <div className="rounded-xl p-4 mb-3 text-center text-[12px]" style={{ background: "var(--panel-2)", border: "1px dashed var(--border)", color: "var(--text-muted)" }}>
          {plan?.invalidation || "No high-probability setup right now."}
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={onRecalculate} disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-[12px] font-semibold ym-hover"
          style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Hitung Ulang
        </button>
        <button onClick={save}
          className="grid place-items-center rounded-lg ym-hover" style={{ width: 42, background: "var(--panel-2)", border: "1px solid var(--border)" }} title="Simpan Rencana">
          <Save size={15} style={{ color: "var(--accent-soft)" }} />
        </button>
      </div>
    </div>
  );
}

function InfoBox({ icon: Icon, label, value, sub, highlight }) {
  return (
    <div className="rounded-lg p-2.5" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-1.5 text-[9px] font-semibold tracking-wide mb-1" style={{ color: "var(--text-dim)" }}>
        <Icon size={12} /> {label.toUpperCase()}
      </div>
      <div className="text-[12px] font-bold">{value}</div>
      <div className="text-[10px] tabnum" style={{ color: highlight ? "var(--accent-soft)" : "var(--text-dim)" }}>{sub}</div>
    </div>
  );
}

function Row({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-2 text-[12px]" style={{ color: "var(--text-muted)" }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: color }} />{label}
      </div>
      <div className={`tabnum text-[13px] ${bold ? "font-extrabold" : "font-bold"}`} style={{ color: bold ? color : "var(--text)" }}>{value}</div>
    </div>
  );
}
